/**
 * Recharge Subscription Widget — PDP bridge + light-DOM purchase mask
 * (pills, Size Select2, Frequency Select2 mirrored from rc-selling-plans shadow DOM).
 */

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function getInnerFrequencySelect(rootElement) {
  const sellingPlans = rootElement.querySelector('rc-selling-plans');
  if (!sellingPlans?.shadowRoot) return null;
  const dropdown = sellingPlans.shadowRoot.querySelector('.rc-plans-dropdown');
  return dropdown?.querySelector('.rc-plans-dropdown__select') ?? null;
}

function getInnerFrequencyLabel(rootElement) {
  const sellingPlans = rootElement.querySelector('rc-selling-plans');
  if (!sellingPlans?.shadowRoot) return null;
  return sellingPlans.shadowRoot.querySelector('.rc-plans__label') ?? null;
}

window.spRecharge = {
  getRoot() {
    const w = document.querySelector('recharge-subscription-widget');
    return w?.shadowRoot?.querySelector('.rc-widget__root') ?? null;
  },
  getFrequencySelect(root) { return getInnerFrequencySelect(root); },
  getFrequencyLabel(root) { return getInnerFrequencyLabel(root); },
  isSubscriptionSelected(root) {
    return !!root?.querySelector('.rc-purchase-option__subscription[rc-selected]');
  },
};

function shouldShowAtcPrice() {
  return document.querySelector('.product.main-product')?.dataset.showAtcPrice !== 'false';
}

function getAddToCartLabel(price) {
  if (!shouldShowAtcPrice() || !price) return window.langMessages['products.product.add_to_cart'];
  return `${window.langMessages['products.product.add_to_cart']} - ${price.replace('.00', '')}`;
}

function formatVariantPrice(amount) {
  const currency = window.Shopify?.currency?.active || 'USD';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
}

// Shopify server-renders the add-to-cart button's data-price with the shop's own money
// format (e.g. "Afl. 30"), which is always correct for the active market — unlike
// Intl.NumberFormat's narrowSymbol, which falls back to the raw ISO code (e.g. "AWG")
// for currencies it has no symbol glyph for. Reuse that notation instead of Intl's.
function getServerFormattedPriceSample() {
  return document.querySelector('[id*="product-form-"]')?.querySelector('[name="add"]')?.dataset?.price || null;
}

function formatAmountWithNotation(sample, amount) {
  if (!sample) return null;
  const numberText = amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const prefix = sample.match(/^[^\d]*/)?.[0] ?? '';
  const suffix = sample.match(/[^\d]*$/)?.[0] ?? '';
  return `${prefix}${numberText}${suffix}`;
}

function parsePriceValue(price) {
  if (!price) return null;
  const parsedPrice = Number.parseFloat(String(price).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsedPrice) ? parsedPrice : null;
}

function updateDiscountPercentage(priceElement, regularPrice, salePrice) {
  const priceWrapper = priceElement?.closest('.price');
  if (!priceWrapper) return;

  const regularPriceValue = parsePriceValue(regularPrice);
  const salePriceValue = parsePriceValue(salePrice);
  const discountValue = (
    regularPriceValue && salePriceValue && regularPriceValue > salePriceValue
  )
    ? Math.round(((regularPriceValue - salePriceValue) / regularPriceValue) * 100)
    : 0;

  const existingDiscount = priceWrapper.querySelector('.price__discount-percentage');

  if (discountValue <= 0) {
    existingDiscount?.remove();
    return;
  }

  const discountContainer = existingDiscount || document.createElement('div');
  if (!existingDiscount) {
    discountContainer.classList.add('price__discount-percentage');
    const discountText = document.createElement('dd');
    discountContainer.appendChild(discountText);
    priceWrapper.appendChild(discountContainer);
  }

  const discountText = discountContainer.querySelector('dd');
  if (discountText) discountText.textContent = `(${discountValue}% OFF)`;
}

function clickSubscriptionInput(rootElement) {
  rootElement.querySelector('.rc-purchase-option__subscription input[value="subscription"]')?.click();
}

function clickOnetimeInput(rootElement) {
  rootElement.querySelector('.rc-selection__onetime input[type="radio"]')?.click()
    || rootElement.querySelector('input[value="onetime"]')?.click()
    || rootElement.querySelector('.rc-purchase-option__onetime input')?.click();
}

/**
 * Texto del pill: único nodo de texto **directo** hijo de `.rc-purchase-option__selector`
 * (Recharge guarda ahí el copy que configuran; el resto suele ser precio en nodos hijos).
 */
function extractPurchaseOptionLabel(optionEl) {
  if (!optionEl) return '';
  const selector = optionEl.querySelector('.rc-purchase-option__selector');
  if (!selector) return '';

  for (const node of selector.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node.nodeValue.replace(/\s+/g, ' ').trim();
      if (t) return t;
    }
  }
  return '';
}

function syncPillLabelsFromWidget(rootElement, mask) {
  const subOpt = rootElement.querySelector('.rc-purchase-option__subscription');
  let onetimeOpt = rootElement.querySelector('.rc-purchase-option__onetime');
  if (!onetimeOpt) {
    const otHost = rootElement.querySelector('.rc-selection__onetime');
    onetimeOpt = otHost?.closest('.rc-purchase-option') ?? null;
  }
  if (!onetimeOpt) {
    onetimeOpt = [...rootElement.querySelectorAll('.rc-purchase-option')].find(
      (el) => !el.matches('.rc-purchase-option__subscription'),
    ) ?? null;
  }

  const subText = extractPurchaseOptionLabel(subOpt);
  const otText = extractPurchaseOptionLabel(onetimeOpt);

  const otBtn = mask.querySelector('.sp-purchase-options__pill[data-mode="onetime"]');
  const subBtn = mask.querySelector('.sp-purchase-options__pill[data-mode="subscription"]');
  const otSpan = otBtn?.querySelector('.js-purchase-pill-label');
  const subSpan = subBtn?.querySelector('.js-purchase-pill-label');

  if (otText && otSpan) {
    otSpan.textContent = otText;
    otBtn?.setAttribute('aria-label', otText);
  }
  if (subText && subSpan) {
    subSpan.textContent = subText;
    subBtn?.setAttribute('aria-label', subText);
  }
}

function updatePillsUI(mask, mode) {
  mask.querySelectorAll('.sp-purchase-options__pill[data-mode]').forEach((btn) => {
    const active = btn.dataset.mode === mode;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-selected', active ? 'true' : 'false');
  });
}

function mirrorFrequencyOptions(rootElement, mask) {
  const lightLabel = mask.querySelector('.js-frequency-label');
  const innerLabel = getInnerFrequencyLabel(rootElement);
  const innerLabelText = innerLabel?.textContent?.replace(/\s+/g, ' ').trim();
  if (lightLabel && innerLabelText) {
    lightLabel.textContent = innerLabelText;
  }

  const inner = getInnerFrequencySelect(rootElement);
  const light = mask.querySelector('.js-frequency-select');
  if (!inner || !light || typeof window.$ === 'undefined' || !$.fn.select2) return;

  const sellingPlanInput = document.querySelector('input[name="selling_plan"]');
  const preserve = sellingPlanInput?.value || inner.value;

  light.innerHTML = inner.innerHTML;

  if ($(light).hasClass('select2-hidden-accessible')) {
    $(light).select2('destroy');
  }

  const dropdownParent = mask.closest('.product__info-wrapper') || mask;

  $(light).select2({
    dropdownParent: $(dropdownParent),
    minimumResultsForSearch: Infinity,
  });

  $(light).off('select2:close.spFreq').on('select2:close.spFreq', () => {
    inner.value = light.value;
    inner.dispatchEvent(new Event('change', { bubbles: true }));
  });

  if (preserve) {
    const optExists = [...light.options].some((o) => o.value === String(preserve));
    if (optExists) {
      light.value = preserve;
      inner.value = preserve;
      $(light).val(String(preserve)).trigger('change.select2');
    }
  }
}

function syncLightFrequencyFromSellingPlan(mask, value) {
  const light = mask?.querySelector('.js-frequency-select');
  if (!light || value === undefined || value === null) return;
  const str = String(value);
  const optExists = [...light.options].some((o) => o.value === str);
  if (!optExists) return;
  if (light.value === str) return;
  light.value = str;
  if (typeof window.$ !== 'undefined' && $(light).hasClass('select2-hidden-accessible')) {
    $(light).val(str).trigger('change.select2');
  }
}

function syncMaskFromWidget(rootElement, mask) {
  syncPillLabelsFromWidget(rootElement, mask);

  const subOpt = rootElement.querySelector('.rc-purchase-option__subscription[rc-selected]');
  if (subOpt) {
    mask.classList.add('sp-purchase-options--subscription-active');
    updatePillsUI(mask, 'subscription');
    mirrorFrequencyOptions(rootElement, mask);
  } else {
    mask.classList.remove('sp-purchase-options--subscription-active');
    updatePillsUI(mask, 'onetime');
  }
}

function pollSellingPlansAndMirror(rootElement, mask, onReady) {
  let attempts = 0;
  const tick = () => {
    const inner = getInnerFrequencySelect(rootElement);
    if (inner) {
      mirrorFrequencyOptions(rootElement, mask);
      onReady?.();
      return;
    }
    if (attempts++ < 200) {
      setTimeout(tick, 50);
    } else {
      onReady?.();
    }
  };
  tick();
}

function onPillSubscription(rootElement, mask) {
  clickSubscriptionInput(rootElement);
  mask.classList.add('sp-purchase-options--subscription-active');
  updatePillsUI(mask, 'subscription');
  pollSellingPlansAndMirror(rootElement, mask, () => {
    mirrorFrequencyOptions(rootElement, mask);
  });
}

function onPillOnetime(rootElement, mask) {
  clickOnetimeInput(rootElement);
  mask.classList.remove('sp-purchase-options--subscription-active');
  updatePillsUI(mask, 'onetime');
}

function setupSizeSelect2(mask) {
  if (!mask || typeof window.$ === 'undefined' || !$.fn.select2) return;
  const sizeSelect = mask.querySelector('.js-size-select');
  if (!sizeSelect || sizeSelect.dataset.spSelect2Ready === 'true') return;
  sizeSelect.dataset.spSelect2Ready = 'true';

  const dropdownParent = mask.closest('.product__info-wrapper') || mask;

  $(sizeSelect).select2({
    dropdownParent: $(dropdownParent),
    minimumResultsForSearch: Infinity,
  });

  $(sizeSelect).off('select2:select.spSize').on('select2:select.spSize', () => {
    const val = $(sizeSelect).val();
    const radio =
      mask.querySelector(`input[name="variant-size-id"][data-value="${val}"]:not(:disabled)`) ||
      mask.querySelector(`input[name="variant-size-id"][data-value="${val}"]`);
    if (radio && !radio.disabled) {
      radio.click();
    }
  });

  mask.querySelectorAll('input[name="variant-size-id"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      if (!radio.checked) return;
      const h = radio.dataset.value;
      if ($(sizeSelect).val() !== h) {
        $(sizeSelect).val(h).trigger('change.select2');
      }
    });
  });
}

function exposePurchaseOptionsApi(ctx) {
  const { rootElement, mask } = ctx;
  window.spPurchaseOptions = {
    setMode(mode) {
      if (!rootElement || !mask) return;
      if (mode === 'subscription') onPillSubscription(rootElement, mask);
      else onPillOnetime(rootElement, mask);
    },
    setFrequency(planId) {
      const inner = getInnerFrequencySelect(rootElement);
      const light = mask?.querySelector('.js-frequency-select');
      if (!inner || !light) return;
      inner.value = String(planId);
      inner.dispatchEvent(new Event('change', { bubbles: true }));
      mirrorFrequencyOptions(rootElement, mask);
    },
    setSize(handleValue) {
      const radio = mask?.querySelector(`input[name="variant-size-id"][data-value="${handleValue}"]`);
      if (radio && !radio.disabled) radio.click();
    },
    getRootElement() {
      return rootElement;
    },
    getMask() {
      return mask;
    },
  };
}

function setupSellingPlanObserver(rootElement, mask, sellingPlanInput) {
  const quantityInput = sellingPlanInput.parentElement?.querySelector('input[name="quantity"]');

  const sellingPlanObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'value') {
        setTimeout(() => {
          if (quantityInput) quantityInput.dataset.sellingplan = sellingPlanInput.value;
          updateGeneralPrices(rootElement, sellingPlanInput.value);
          syncLightFrequencyFromSellingPlan(mask, sellingPlanInput.value);
        }, 100);
      }
    });
  });

  sellingPlanObserver.observe(sellingPlanInput, { attributes: true });
}

function setupPurchaseBridge(ctx) {
  const { rootElement, mask } = ctx;

  mask.querySelectorAll('.sp-purchase-options__pill[data-mode]').forEach((pill) => {
    pill.addEventListener('click', () => {
      const mode = pill.dataset.mode;
      if (mode === 'subscription') onPillSubscription(rootElement, mask);
      else onPillOnetime(rootElement, mask);
    });
  });

  rootElement.querySelectorAll('.rc-purchase-option').forEach((opt) => {
    const mo = new MutationObserver(() => {
      syncMaskFromWidget(rootElement, mask);
    });
    mo.observe(opt, { attributes: true, attributeFilter: ['rc-selected'] });
  });

  const debouncedMirror = debounce(() => mirrorFrequencyOptions(rootElement, mask), 200);

  let innerOptionsObserverAttached = false;

  const rootMo = new MutationObserver(() => {
    const inner = getInnerFrequencySelect(rootElement);
    if (inner && !innerOptionsObserverAttached) {
      innerOptionsObserverAttached = true;
      const mo = new MutationObserver(() => debouncedMirror());
      mo.observe(inner, { childList: true, subtree: true });
    }
    if (inner) debouncedMirror();
  });
  rootMo.observe(rootElement, { childList: true, subtree: true });

  document.querySelector('.product.main-product hh-variant-picker')?.addEventListener('change', () => {
    const variantIdInput = document.querySelector('[id^="product-form-"] input[name="id"]');
    const variantsJson = document.querySelector('.sp-product-variant-size script[type="application/json"]');
    if (variantIdInput && variantsJson) {
      try {
        const variants = JSON.parse(variantsJson.textContent);
        const selected = variants.find((v) => String(v.id) === String(variantIdInput.value));
        if (selected?.price != null) window.variantPrice = selected.price / 100;
      } catch (_) {}
    }

    setTimeout(() => {
      mirrorFrequencyOptions(rootElement, mask);
      syncMaskFromWidget(rootElement, mask);
      const isSubActive = !!rootElement.querySelector('.rc-purchase-option__subscription[rc-selected]');
      if (isSubActive) {
        const spi = document.querySelector('input[name="selling_plan"]');
        if (spi) updateGeneralPrices(rootElement, spi.value);
      }
    }, 500);
  });

  exposePurchaseOptionsApi(ctx);
}

/**
 * Pills must not wait for rc-selling-plans: that block often does not exist until "Subscription"
 * is selected, so polling for the inner <select> blocked init for up to ~10s.
 */
function finishBridgeInit(rootElement, mask) {
  mask.dataset.bridgeReady = 'true';

  syncMaskFromWidget(rootElement, mask);

  const spi = document.querySelector('input[name="selling_plan"]');
  if (spi) {
    const qty = spi.parentElement?.querySelector('input[name="quantity"]');
    if (qty) qty.dataset.sellingplan = spi.value;
  }

  const variantPicker = document.querySelector('hh-variant-picker');
  variantPicker?.dispatchEvent(new Event('change', { bubbles: true }));

  window.subscriptionWidgetLoaded = true;

  // If subscription is default, frequency UI may mount a few ticks after the programmatic click.
  mirrorFrequencyOptions(rootElement, mask);
  requestAnimationFrame(() => {
    mirrorFrequencyOptions(rootElement, mask);
    setTimeout(() => mirrorFrequencyOptions(rootElement, mask), 100);
  });
}

function bootstrapRechargeMask(rechargeWidget) {
  rechargeWidget.classList.add('sp-recharge-widget--masked');

  const mask = document.querySelector('.js-sp-purchase-options');

  if (!mask || mask.dataset.hasSubscription !== 'true') {
    window.subscriptionWidgetLoaded = true;
    return;
  }

  setupSizeSelect2(mask);

  let checkAttempts = 0;
  const checkContent = setInterval(() => {
    const rootElement = rechargeWidget.shadowRoot?.querySelector('.rc-widget__root');

    if (!rootElement) {
      if (++checkAttempts >= 300) {
        clearInterval(checkContent);
        window.subscriptionWidgetLoaded = true;
      }
      return;
    }

    clearInterval(checkContent);

    const badge = rootElement.querySelector('.rc-purchase-option__badge');
    badge?.remove();

    const ctx = {
      rechargeWidget,
      rootElement,
      mask,
    };

    setupPurchaseBridge(ctx);

    const subscriptionDefault = document.querySelector('.product.main-product')?.dataset.subscriptionDefault === 'true';
    if (subscriptionDefault) {
      clickSubscriptionInput(rootElement);
    }

    finishBridgeInit(rootElement, mask);

    let sellingPlanObserverAttached = false;
    const tryAttachSellingPlanObserver = () => {
      if (sellingPlanObserverAttached) return true;
      const sellingPlanInput = document.querySelector('input[name="selling_plan"]');
      if (!sellingPlanInput) return false;
      sellingPlanObserverAttached = true;
      setupSellingPlanObserver(rootElement, mask, sellingPlanInput);
      const qty = sellingPlanInput.parentElement?.querySelector('input[name="quantity"]');
      if (qty) qty.dataset.sellingplan = sellingPlanInput.value;
      const isSubscriptionSelected = !!rootElement.querySelector('.rc-purchase-option__subscription[rc-selected]');
      if (isSubscriptionSelected && sellingPlanInput.value) {
        updateGeneralPrices(rootElement, sellingPlanInput.value);
      }
      return true;
    };

    if (!tryAttachSellingPlanObserver()) {
      let mo = null;
      mo = new MutationObserver(() => {
        if (tryAttachSellingPlanObserver()) mo.disconnect();
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });

      let attempts = 0;
      const poll = setInterval(() => {
        if (tryAttachSellingPlanObserver()) {
          clearInterval(poll);
          mo.disconnect();
        } else if (attempts++ >= 80) {
          clearInterval(poll);
          mo.disconnect();
        }
      }, 50);
    }

    // Re-sync prices after RC widget has had time to load the correct variant's prices.
    // Covers the race condition on first page load where subscription is active by default.
    setTimeout(() => {
      const spi = document.querySelector('input[name="selling_plan"]');
      const isSubSelected = !!rootElement.querySelector('.rc-purchase-option__subscription[rc-selected]');
      if (isSubSelected && spi) updateGeneralPrices(rootElement, spi.value);
    }, 600);
  }, 10);
}

function updateGeneralPrices(rootElement, sellingPlanSelected) {
  let newPrice;
  let rcOriginalPrice;

  const selectedOption = rootElement.querySelector('.rc-purchase-option[rc-selected]');

  selectedOption?.querySelector('.rc-price:not(.strike-through)')?.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.nodeValue)) {
      newPrice = node.nodeValue.replace(/\b[A-Z]{3}\b/g, '').trim();
    }
  });

  // Read the RC widget's own original price (rendered as its internal strikethrough).
  // When present, both newPrice and rcOriginalPrice come from the same widget state,
  // so they're always in sync — avoids mismatches during variant/subscription race conditions.
  selectedOption?.querySelector('.rc-price.strike-through')?.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.nodeValue)) {
      rcOriginalPrice = node.nodeValue.replace(/\b[A-Z]{3}\b/g, '').trim().replace('.00', '');
    }
  });

  if (!newPrice) return;

  // Prefer the RC widget's own original price as the reference when available.
  // Fall back to window.variantPrice, formatted with the market's real currency notation.
  const referencePrice = rcOriginalPrice
    || formatAmountWithNotation(getServerFormattedPriceSample(), window.variantPrice)
    || formatVariantPrice(window.variantPrice);

  const sectionId = document.querySelector('.product.main-product')?.dataset?.sectionId;

  const addButton = document
    .querySelector('[id*="product-form-"]')
    ?.querySelector('[name="add"]');

  if (addButton) {
    const btnPriceSpan = sectionId
      ? addButton.querySelector(`.js-button-price-${sectionId}`)
      : null;
    if (btnPriceSpan) {
      btnPriceSpan.textContent = `- ${newPrice.replace('.00', '')}`;
    } else {
      addButton.textContent = getAddToCartLabel(newPrice);
    }
  }

  const stickyButtonPrice = document.querySelector('.js-sp-sticky-button-price');
  if (stickyButtonPrice) {
    stickyButtonPrice.textContent = `- ${newPrice.replace('.00', '')}`;
  }
  const priceRoots = sectionId
    ? document.querySelectorAll(`[data-pdp-price="${sectionId}"]`)
    : document.querySelectorAll('.product__price')

  priceRoots.forEach((priceRoot) => {
    const overallPrice = priceRoot.querySelector(
      '.price div:not(.price__strikethrough):not(.price__discount-percentage) dd'
    )
    const originalPrice = priceRoot.querySelector('.price div.price__strikethrough dd')
    const customStrikethroughPrice = document.createElement('div')
    customStrikethroughPrice.classList.add('price__strikethrough')

    if (overallPrice) overallPrice.textContent = newPrice.replace('.00', '')

    if (referencePrice != newPrice.replace('.00', '')) {
      if (originalPrice) {
        originalPrice.textContent = referencePrice
      } else {
        const priceElement = document.createElement('dd')
        priceElement.textContent = referencePrice
        customStrikethroughPrice.appendChild(priceElement)

        overallPrice?.closest('.price')?.append(customStrikethroughPrice)
      }
    } else if (originalPrice) {
      originalPrice.remove()
    }

    updateDiscountPercentage(overallPrice, referencePrice, newPrice.replace('.00', ''))
  })

  const ATCForm = document.querySelector('[id*=atc-form-]');
  if (ATCForm) ATCForm.dataset.sellingplanid = sellingPlanSelected;
}

// ─── Legacy widget styling (restored from sp-subscriptions.js, lost in rebuild) ───

function legacyApplyStylesToWidget() {
  let legacyWidgetCounter = 0;
  const legacyWidgetInterval = setInterval(() => {
    const rechargeWidget = document.querySelector('recharge-subscription-widget');
    if (!rechargeWidget) {
      if (legacyWidgetCounter++ < 150) return;
      clearInterval(legacyWidgetInterval);
      return;
    }
    clearInterval(legacyWidgetInterval);

    let legacyRootCounter = 0;
    const legacyRootInterval = setInterval(() => {
      const rootElement = rechargeWidget.shadowRoot?.querySelector('.rc-widget__root');
      if (!rootElement) {
        if (legacyRootCounter++ < 150) return;
        clearInterval(legacyRootInterval);
        return;
      }
      clearInterval(legacyRootInterval);

      // Move discount badge text into subscription title, remove original badge.
      const maxDiscountText = rootElement.querySelector('.rc-purchase-option__badge');
      const subscribeTitle = rootElement.querySelector('.rc-purchase-option__subscription .rc-purchase-option__selector');
      if (maxDiscountText && subscribeTitle) {
        const percentageSpan = document.createElement('span');
        percentageSpan.innerText = maxDiscountText.innerText;
        percentageSpan.style.marginLeft = '4px';
        subscribeTitle.appendChild(percentageSpan);
        maxDiscountText.remove();
      }

      const style = document.createElement('style');
      style.textContent = `
        .rc-purchase-option { border: solid 1px transparent !important; }
        .rc-price, .rc-purchase-option__selector { font-weight: 400 !important; font-size: 14px !important; }
        .rc-purchase-option[rc-selected] { border: solid 1px #2B2829 !important; }
        .rc-purchase-option:not([rc-selected]):hover { background: #2B2829; color: #FAF9F8; }
        .rc-purchase-option__label { padding: 10px 15px !important; }
        .rc-purchase-option:not(:last-child) { margin-bottom: 0 !important; }
        .rc-price.strike-through { display: none !important; }
        .rc-purchase-option__checked-indicator { position: relative; background: white; border-radius: 50%; }
        .rc-purchase-option__checked-indicator[checked]::after {
          content: "";
          width: 9.3px;
          height: 9.3px;
          background: #AD7821;
          border-radius: 50%;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        .rc-selection__onetime .rc-purchase-option__sub-container { display: none; }
        rc-benefits { order: 1; font-size: 14px !important; }
        .rc-purchase-option__sub-container { padding: 0 15px 10px !important; }
        .rc-purchase-option__subscription { transition: all .2s ease; }
        .rc-purchase-option__subscription .rc-purchase-option__sub-container { opacity: 0; transition: all .1s ease; }
      `;
      rechargeWidget.shadowRoot.appendChild(style);

      if (typeof window.$ !== 'undefined') {
        $(rootElement).on('click', '.rc-purchase-option__subscription:not([rc-selected])', () => {
          legacyOnSubscriptionOptionSelection(rootElement);
        });
      }

      // Observe selling_plan input to update prices when switching one-time ↔ subscription.
      const tryAttachLegacySellingPlanObserver = () => {
        const spi = document.querySelector('input[name="selling_plan"]');
        if (!spi) return false;
        const qty = spi.parentElement?.querySelector('input[name="quantity"]');
        const legacySPObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'value') {
              setTimeout(() => {
                if (qty) qty.dataset.sellingplan = spi.value;
                updateGeneralPrices(rootElement, spi.value);
              }, 100);
            }
          });
        });
        legacySPObserver.observe(spi, { attributes: true });
        return true;
      };

      if (!tryAttachLegacySellingPlanObserver()) {
        let spMo = null;
        spMo = new MutationObserver(() => {
          if (tryAttachLegacySellingPlanObserver()) spMo.disconnect();
        });
        spMo.observe(document.documentElement, { childList: true, subtree: true });
      }

      // Update subscription price when variant changes (mirrors setupPurchaseBridge in pills mode).
      document.querySelector('.product.main-product hh-variant-picker')?.addEventListener('change', () => {
        const variantIdInput = document.querySelector('[id^="product-form-"] input[name="id"]');
        const variantsJson = document.querySelector('.sp-product-variant-size script[type="application/json"]');
        if (variantIdInput && variantsJson) {
          try {
            const variants = JSON.parse(variantsJson.textContent);
            const selected = variants.find((v) => String(v.id) === String(variantIdInput.value));
            if (selected?.price != null) window.variantPrice = selected.price / 100;
          } catch (_) {}
        }
        setTimeout(() => {
          const isSubActive = !!rootElement.querySelector('.rc-purchase-option__subscription[rc-selected]');
          if (isSubActive) {
            const spi = document.querySelector('input[name="selling_plan"]');
            if (spi) updateGeneralPrices(rootElement, spi.value);
          }
        }, 500);
      });
    }, 10);
  }, 50);
}

function legacyOnSubscriptionOptionSelection(rootElement) {
  let sellingPlansCounter = 0;
  const checkSellingPlans = setInterval(() => {
    const sellingPlans = rootElement.querySelector('rc-selling-plans');
    if (sellingPlans) {
      const sellingPlansRoot = sellingPlans.shadowRoot?.querySelector('.rc-plans-dropdown');
      if (sellingPlansRoot) {
        clearInterval(checkSellingPlans);
        legacyCustomSelectorJQ(sellingPlansRoot, sellingPlans);

        const sellingPlansStyles = document.createElement('style');
        sellingPlansStyles.textContent = `
          .rc-plans__label { color: #6A6868 !important; font-size: 12px !important; }
          .rc-plans-dropdown__icon { display: none; }
        `;
        sellingPlans.shadowRoot.appendChild(sellingPlansStyles);
        sellingPlans.parentElement.style.opacity = '1';
      }
    } else if (sellingPlansCounter++ < 150) {
      return;
    } else {
      clearInterval(checkSellingPlans);
    }
  }, 10);
}

function legacyCustomSelectorJQ(sellingPlansRoot, sellingPlans) {
  $(sellingPlansRoot).find('.rc-plans-dropdown__select').wrap('<div class="select2-wrapper"></div>');
  $(sellingPlansRoot).find('.rc-plans-dropdown__select').select2({
    dropdownParent: $(sellingPlansRoot).find('.rc-plans-dropdown__select').parent(),
  });
  $(sellingPlansRoot).find('.rc-plans-dropdown__select').on('select2:close', function () {
    sellingPlansRoot.querySelector('.rc-plans-dropdown__select').dispatchEvent(new Event('change'));
  });

  const select2LinkOriginal = document.querySelector('link[href*="component-select2"]');
  const spGlobalLinkOriginal = document.querySelector('link[href*="sp-global.css"]');
  if (select2LinkOriginal) {
    const dup = document.createElement('link');
    dup.rel = 'stylesheet';
    dup.href = select2LinkOriginal.href;
    sellingPlans.shadowRoot.appendChild(dup);
  }
  if (spGlobalLinkOriginal) {
    const dup = document.createElement('link');
    dup.rel = 'stylesheet';
    dup.href = spGlobalLinkOriginal.href;
    sellingPlans.shadowRoot.appendChild(dup);
  }
}

window.addEventListener('DOMContentLoaded', () => {
  window.subscriptionWidgetLoaded = false;

  const productElLegacyCheck = document.querySelector('.product.main-product');
  if (productElLegacyCheck && productElLegacyCheck.dataset.pillsVersion !== 'true') {
    window.subscriptionWidgetLoaded = true;
    legacyApplyStylesToWidget();
    return; // legacy: native widget visible, no mask/bridge
  }

  const mask = document.querySelector('.js-sp-purchase-options');
  if (mask) {
    setupSizeSelect2(mask);
    if (mask.dataset.hasSubscription === 'false') {
      window.subscriptionWidgetLoaded = true;
    }
  }

  let subscriptionCounter = 0;

  const subscriptionInterval = setInterval(() => {
    const rechargeWidget = document.querySelector('recharge-subscription-widget');

    if (rechargeWidget) {
      const productEl = document.querySelector('.product.main-product');
      if (productEl?.dataset.disablePurchasing === 'true') {
        rechargeWidget.style.display = 'none';
        clearInterval(subscriptionInterval);
        window.subscriptionWidgetLoaded = true;
        return;
      }
      bootstrapRechargeMask(rechargeWidget);
      clearInterval(subscriptionInterval);
    } else if (!rechargeWidget && subscriptionCounter < 150) {
      subscriptionCounter += 1;
    } else {
      clearInterval(subscriptionInterval);
      window.subscriptionWidgetLoaded = true;
    }
  }, 50);
});
